/**
 * Payment module DEMO harness — a scripted mock of the backend + native sheet so
 * the full payment flow (sheet → processing → success/pending/failed → retry) can
 * be exercised on-device with NO backend, HitPay keys, or network.
 *
 * It is inert unless `startPaymentDemo()` is called (from the /payment-demo page),
 * so production/real payments are never affected — the api/stripe utils only
 * delegate here when `isPaymentDemoActive()` is true.
 *
 * Each scenario maps to one of the cases walked through in design:
 *   1 happy               — paid; webhook confirms immediately.
 *   2 fail_webhook        — sheet completes but the webhook reports FAILED.
 *   3 success_no_webhook  — paid at HitPay, webhook never arrives → reconcile catches PAID.
 *   4 fail_no_webhook     — no webhook → reconcile finds it never completed → EXPIRED.
 *   5 fail_then_retry     — attempt 1 = case 4; a manual retry (attempt 2) succeeds.
 */

export const PaymentDemoScenario = {
  HAPPY: 'happy',
  FAIL_WEBHOOK: 'fail_webhook',
  SUCCESS_NO_WEBHOOK: 'success_no_webhook',
  FAIL_NO_WEBHOOK: 'fail_no_webhook',
  FAIL_THEN_RETRY: 'fail_then_retry',
  NO_LINKED_ACCOUNTS: 'no_linked_accounts',
};

// UI copy for the demo page (order = the 5 cases).
export const PAYMENT_DEMO_SCENARIOS = [
  {
    key: PaymentDemoScenario.HAPPY,
    title: '1 · Happy flow',
    desc: 'Buys and gets it — the webhook confirms PAID right away. Success screen.',
  },
  {
    key: PaymentDemoScenario.FAIL_WEBHOOK,
    title: '2 · Fails (webhook reports back)',
    desc: 'Sheet completes but the payment did not go through; the webhook flips it FAILED → failure screen (Try again available).',
  },
  {
    key: PaymentDemoScenario.SUCCESS_NO_WEBHOOK,
    title: '3 · Succeeds, webhook never returns',
    desc: 'Paid at HitPay but no webhook — the poll stays pending, then the on-demand reconcile catches PAID → success.',
  },
  {
    key: PaymentDemoScenario.FAIL_NO_WEBHOOK,
    title: '4 · Fails, webhook never returns',
    desc: 'No webhook — poll pending, then reconcile finds the intent never completed → EXPIRED → failure screen.',
  },
  {
    key: PaymentDemoScenario.FAIL_THEN_RETRY,
    title: '5 · Fail → manual retry',
    desc: 'First attempt fails like case 4. On the failure screen tap “Try again”, then the card tile — the retry attempt succeeds.',
  },
  {
    key: PaymentDemoScenario.NO_LINKED_ACCOUNTS,
    title: '6 · No linked accounts',
    desc: 'Opens with NO linked accounts, so card / PayNow is the whole offer — there is no longer a “Link a bank account” tile to start eGIRO setup mid-payment. Pay by card.',
  },
];

const state = { active: false, scenario: null, attempt: 0 };

/** Turn on demo mode for a scenario (resets the attempt counter). */
export function startPaymentDemo(scenario) {
  state.active = true;
  state.scenario = scenario;
  state.attempt = 0;
}

/** Turn demo mode off — real payment path resumes. */
export function stopPaymentDemo() {
  state.active = false;
  state.scenario = null;
  state.attempt = 0;
}

export function isPaymentDemoActive() {
  return state.active;
}

export function activePaymentDemoScenario() {
  return state.scenario;
}

/**
 * Fast poll timings for demo mode so scenarios resolve in a couple of seconds
 * instead of the real ~20s reconcile window / ~90s cap.
 */
export function paymentDemoPollTimings() {
  return { intervalMs: 700, reconcileAfterMs: 2200, maxMs: 8000 };
}

function shape(reference, status) {
  return {
    reference,
    status,
    amountCents: 2500,
    currency: 'SGD',
    productType: 'course-program',
    productId: 'first-steps-demo',
  };
}

/** Mock for createPaymentTransaction — always opens (each call = a new attempt). */
export function demoCreateTransaction() {
  state.attempt += 1;
  return {
    reference: `demo_${state.scenario}_${state.attempt}`,
    status: 'PENDING',
    clientSecret: `demo_cs_${state.attempt}`,
  };
}

/** Mock for the native sheet — in every case the user submits and it completes. */
export function demoPresentSheet() {
  return 'completed'; // === HitPayCheckoutResult.RETURNED
}

/** Mock for the ledger poll (GET). Returns the status the webhook would have set. */
export function demoGetTransaction(reference) {
  switch (state.scenario) {
    case PaymentDemoScenario.HAPPY:
    case PaymentDemoScenario.NO_LINKED_ACCOUNTS:
      return shape(reference, 'PAID');
    case PaymentDemoScenario.FAIL_WEBHOOK:
      return shape(reference, 'FAILED');
    case PaymentDemoScenario.FAIL_THEN_RETRY:
      // Attempt 1 lingers PENDING (→ reconcile EXPIRED); the retry (attempt 2) pays.
      return shape(reference, state.attempt >= 2 ? 'PAID' : 'PENDING');
    case PaymentDemoScenario.SUCCESS_NO_WEBHOOK:
    case PaymentDemoScenario.FAIL_NO_WEBHOOK:
    default:
      // Webhook never lands — stay PENDING so the on-demand reconcile decides.
      return shape(reference, 'PENDING');
  }
}

/** Mock for the on-demand reconcile — what HitPay "really" says when asked. */
export function demoReconcileTransaction(reference) {
  switch (state.scenario) {
    case PaymentDemoScenario.SUCCESS_NO_WEBHOOK:
      return shape(reference, 'PAID');
    case PaymentDemoScenario.FAIL_NO_WEBHOOK:
      return shape(reference, 'EXPIRED');
    case PaymentDemoScenario.FAIL_THEN_RETRY:
      return shape(reference, state.attempt >= 2 ? 'PAID' : 'EXPIRED');
    default:
      // happy / fail_webhook already resolved on the poll; echo current status.
      return demoGetTransaction(reference);
  }
}

/** Mock linked bank accounts so the method step is populated in demo mode. */
export function demoLinkedAccounts() {
  // The "no linked accounts" scenario opens empty so the empty state shows.
  if (state.scenario === PaymentDemoScenario.NO_LINKED_ACCOUNTS) return [];
  return [
    {
      id: 'demo-egiro-1',
      type: 'bank',
      bankCode: 'DBS',
      bankName: 'DBS',
      lastFour: '1234',
      iconPath: '',
      label: 'DBS Account 1234',
      eGiroMandateId: 'demo-egiro-1',
      _raw: { demo: true },
    },
    {
      id: 'demo-egiro-2',
      type: 'bank',
      bankCode: 'OCBC',
      bankName: 'OCBC',
      lastFour: '5678',
      iconPath: '',
      label: 'OCBC Account 5678',
      eGiroMandateId: 'demo-egiro-2',
      _raw: { demo: true },
    },
  ];
}

/**
 * Mock FAST (bank) poll outcome. The bank rail is poll-based with no
 * webhook/reconcile, so scenarios map onto its native SUCCESS / FAILED /
 * PENDING states. Increments the attempt counter so case 5 (retry) succeeds
 * on the second attempt.
 */
export function demoFastStatus() {
  state.attempt += 1;
  switch (state.scenario) {
    case PaymentDemoScenario.HAPPY:
    case PaymentDemoScenario.NO_LINKED_ACCOUNTS:
      return 'SUCCESS';
    case PaymentDemoScenario.FAIL_WEBHOOK:
    case PaymentDemoScenario.FAIL_NO_WEBHOOK:
      return 'FAILED';
    case PaymentDemoScenario.SUCCESS_NO_WEBHOOK:
      return 'PENDING'; // transfer still processing — settles later on My Payments refresh
    case PaymentDemoScenario.FAIL_THEN_RETRY:
      return state.attempt >= 2 ? 'SUCCESS' : 'FAILED';
    default:
      return 'PENDING';
  }
}
